import { DialogContent, Stack } from "@mui/material"
import {
    Button,
    CampaignsFactory,
    CircularProgress,
    DialogActions,
    DialogHandler,
    DialogTitle,
    ETaskType,
    fetchRoute,
    SuppressedEmailsFactory,
    TasksFactory,
    TTask,
    TTaskDownload,
    Typography
} from "@cakemail-org/ui-components-v2"
import { useEffect, useMemo, useRef, useState } from "react"
import "./styles.scss"
import { TShowTaskDialog, TTaskDialogContent } from "./types"
import { useTranslation } from "react-i18next"
import { ERoutes } from "../../../utils/url"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"


export function showTaskDialog({ taskType, context }: TShowTaskDialog) {
    DialogHandler.show({
        content: <TaskDialogContent taskType={taskType} context={context} />,
        managedByContent: true
    })
}

function TaskDialogContent({ taskType, context }: TTaskDialogContent) {
    const { t } = useTranslation()
    const [currentExport, setCurrentExport] = useState<TTask>()
    const callTimeoutRef = useRef<NodeJS.Timeout>(undefined)


    useEffect(() => {
        doExport()
        return () => {
            clearTimeout(callTimeoutRef.current)
        }
    }, [])

    function showError(e: any, btnRef?: TButtonRef) {
        btnRef?.reset()
        DialogHandler.show({
            isExpress: true,
            type: "error",
            title: t("general-error-action")
        })
    }

    function downloadSuccess(data: TTaskDownload, btnRef: TButtonRef) {
        btnRef.reset()
        window.open(data.url, "_blank")
    }
    function doExport() {
        switch (taskType) {
        case ETaskType.campaigns_reports_export:
            CampaignsFactory.createReportsExport({ description: "" }).then((data) => {
                getExport(data.data.id)
            }).catch((e) => {
                showError(e)
            })
            break
        case ETaskType.suppressed_emails_export:
            SuppressedEmailsFactory.createExport().then((data) => {
                getExport(data.data.id)
            }).catch((e) => {
                showError(e)
            })
            break
        }
    }

    function downloadExport(e: React.MouseEvent<HTMLButtonElement>, btnRef: TButtonRef) {
        if (!currentExport) return
        switch (taskType) {
        case ETaskType.campaigns_reports_export:
            CampaignsFactory.downloadReportExport({ exportId: currentExport.id }).then((data) => {
                downloadSuccess(data.data, btnRef)
            }).catch((e) => {
                showError(e)
            })
            break
        case ETaskType.suppressed_emails_export:
            SuppressedEmailsFactory.downloadExport({ exportId: currentExport.id }).then((data) => {
                downloadSuccess(data.data, btnRef)
            }).catch((e) => {
                showError(e)
            })
            break
            // TODO: Add more exporting types
        }
    }

    function getExport(exportId: string) {
        clearTimeout(callTimeoutRef.current)
        TasksFactory.get({ id: exportId }).then((data) => {
            setCurrentExport(data)
            if (!["ready", "failed"].includes(data.status)) {
                setTimeoutGetExport(exportId)
            }
        }).catch((e) => {
            showError(e)
        })
    }

    function setTimeoutGetExport(exportId: string) {
        callTimeoutRef.current = setTimeout(() => {
            getExport(exportId)
        }, 5000)
    }

    function handleViewExports() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.export })
        DialogHandler.hide()
    }

    function getTitleAndDesc() {
        let title = "export-dialog-campaigns-reports-exporting-title"
        let desc = "export-dialog-content-pending"

        switch (taskType) {
        case ETaskType.suppressed_emails_export:
            title = "export-dialog-suppression-list-exporting-title"
            desc = "export-dialog-content-pending"
            break
        case ETaskType.campaign_logs_export:
        case ETaskType.contacts_export:
        case ETaskType.list_logs_export:
        default:
            break
        }

        if (currentExport?.status) {
            if (["ready", "failed", "deleted"].includes(currentExport.status)) {
                title = "export-dialog-campaigns-reports-exported-title"
                desc = "export-dialog-content-exported-desc"

                switch (taskType) {
                case ETaskType.campaign_logs_export:
                    break
                case ETaskType.contacts_export:
                    break
                case ETaskType.list_logs_export:
                    break
                case ETaskType.suppressed_emails_export:
                    title = "export-dialog-suppression-list-exported-title"
                    break
                }
            } else if (currentExport.progress > 0) {
                desc = "export-dialog-content-pending"
            }
        }

        return { titleKey: title, descKey: desc }
    }

    const { titleKey, descKey } = getTitleAndDesc()
    const isReadyToDownload = currentExport?.status && currentExport.status === "ready"
    const circularProgressVariant = currentExport && currentExport.progress > 0 ? "determinate" : "indeterminate"

    return <Stack className="exportDialog">
        <DialogTitle variant="h2">
            <Stack direction="row" alignItems="center" spacing={2}>
                <CircularProgress
                    key={circularProgressVariant}
                    sx={{ marginRight: 4 }}
                    size={32}
                    variant={circularProgressVariant}
                    value={currentExport?.progress || 0}
                />
                {t(titleKey)}
            </Stack>
        </DialogTitle >
        <DialogContent >
            <Typography variant="body1L">
                {t(descKey)}
            </Typography>
        </DialogContent>
        <DialogActions
            primaryBtnText={t(isReadyToDownload ? "general-download" : "general-view-exports")}
            primaryBtnOnClick={isReadyToDownload ? downloadExport : handleViewExports}
            secondaryBtnText={t("general-close")}
        >
            {isReadyToDownload && <Button sx={{ marginRight: "auto!important" }} variant="text" color="body2" onClick={handleViewExports}>
                {t("general-view-exports")}
            </Button>}
        </DialogActions>
    </Stack >
}